/**
 * The Hiring → Applications queue's filter + sort declaration — a consumer of
 * the generic filter toolkit (`components/filters`), mirroring
 * `studentsFilterConfig` / `withdrawalsFilterConfig`.
 *
 * Facets exposed:
 *   - search        → `q`               debounced free text (name or email).
 *   - pipelineStage → `pipelineStage`   multi-select over the 14 pickable stages
 *                                        (every stage EXCEPT `archived`, which is
 *                                        reachable by clearing the facet — see
 *                                        below). Comma-joined; the backend accepts
 *                                        the bar's comma format. The default value
 *                                        replicates the page's prior
 *                                        `ACTIVE_STAGE_DEFAULTS` view (the old
 *                                        `stages` useState initializer). Clearing
 *                                        the facet emits no param (`allEncoding:
 *                                        'omit'`) and the backend then returns ALL
 *                                        stages — this exactly reproduces the old
 *                                        `includeAll` toggle without a bespoke
 *                                        control.
 *   - coursesPreferred → `coursesPreferred` multi-select over the academy's
 *                                        programme slugs (`PROGRAM_OPTIONS`);
 *                                        allEncoding 'omit'.
 *   - hasDocuments  → `hasDocuments`    boolean. `true` = "Has documents",
 *                                        `false` = "Missing documents" — the
 *                                        latter replaces the old `missingDocsOnly`
 *                                        toggle (which sent `hasDocuments=false`).
 *   - submitted     → `submittedFrom` / `submittedTo` date range over the
 *                                        application's `submittedAt` timestamp.
 *
 * The 14-stage option list is DERIVED from `STAGE_LABEL` (single source of truth
 * for stage captions) minus `archived`, so a new pipeline stage shows up here
 * automatically. `defaultValue` is `ACTIVE_STAGE_DEFAULTS` (13 stages; excludes
 * `onboarded` + `archived`) so `defaultValue ⊂ options` always holds.
 *
 * Sorts map to the backend's allow-listed `sort` fields ({submittedAt, fullName}
 * × {asc, desc}); the default (first entry) is "Recently submitted" =
 * submittedAt:desc.
 */

import { z } from 'zod';
import { PROGRAM_OPTIONS } from '../academics/types';
import type { ListFilterConfig, SortColumnBinding } from '../filters';
import {
  ACTIVE_STAGE_DEFAULTS,
  STAGE_LABEL,
  type PipelineStage,
} from './types';

/**
 * The 14 pickable pipeline stages. Derived from the canonical stage-label map
 * so captions stay in sync, minus `archived`: archived applications are the
 * "everything else" bucket surfaced by CLEARING the facet (→ no param → backend
 * returns all stages), which is the exact behaviour the old `includeAll` toggle
 * provided. Casting the key list is safe: `STAGE_LABEL` is typed
 * `Record<PipelineStage, string>`, so its keys ARE the stage union.
 */
const STAGE_OPTIONS = (Object.keys(STAGE_LABEL) as PipelineStage[])
  .filter((stage) => stage !== 'archived')
  .map((stage) => ({ value: stage, label: STAGE_LABEL[stage] }));

export const applicationsFilterConfig: ListFilterConfig = {
  facets: [
    {
      kind: 'search',
      key: 'q',
      label: 'Search',
      placeholder: 'Search by name or email',
    },
    {
      kind: 'multi-select',
      key: 'pipelineStage',
      label: 'Stage',
      group: 'filters',
      // Prior page default = ACTIVE_STAGE_DEFAULTS (the `stages` useState seed):
      // every stage except the terminal `onboarded` + `archived`. Clearing the
      // facet emits no param, and the backend then returns all stages — the old
      // "Include archived" view.
      defaultValue: ACTIVE_STAGE_DEFAULTS,
      allEncoding: 'omit',
      options: STAGE_OPTIONS,
    },
    {
      kind: 'multi-select',
      key: 'coursesPreferred',
      label: 'Course preferred',
      group: 'filters',
      defaultValue: [],
      allEncoding: 'omit',
      options: PROGRAM_OPTIONS.map((c) => ({ value: c.value, label: c.label })),
    },
    {
      kind: 'boolean',
      key: 'hasDocuments',
      label: 'Documents',
      group: 'filters',
      trueLabel: 'Has documents',
      falseLabel: 'Missing documents',
    },
    {
      kind: 'date-range',
      key: 'submitted',
      label: 'Submitted',
      group: 'filters',
      params: { from: 'submittedFrom', to: 'submittedTo' },
    },
  ],
  sorts: [
    { value: 'submittedAt:desc', label: 'Recently submitted' },
    // Registered so the Submitted column header can toggle to ascending; an
    // unregistered `field:dir` would silently fall back to the default sort.
    { value: 'submittedAt:asc', label: 'Oldest first' },
    { value: 'fullName:asc', label: 'Name A–Z' },
    { value: 'fullName:desc', label: 'Name Z–A' },
  ],
  resultNoun: ['application', 'applications'],
};

/**
 * Column-header sort bindings, synced to the same `sort` param. The Candidate
 * column sorts on `fullName`; the Submitted column on `submittedAt`. Both fields
 * have their two directions registered above so headers toggle asc↔desc without
 * snapping back to the default sort.
 */
export const APPLICATIONS_SORT_COLUMNS: Record<
  'fullName' | 'submittedAt',
  SortColumnBinding
> = {
  fullName: { field: 'fullName', firstClickDir: 'asc' },
  submittedAt: { field: 'submittedAt', firstClickDir: 'desc' },
};

/**
 * URL search-param contract for the Applications queue, used as the route's
 * `validateSearch`. The filter toolkit is the single source of truth for these
 * values; this schema validates the raw URL at the route boundary (everything
 * optional, since a bare `/hr/applications` opens on the configured defaults).
 *
 * EVERY param key the config can emit must appear here — `z.object().parse`
 * strips unknown keys, so a missing key would be silently dropped on the next
 * read. In particular `submitted` is the date-range facet's PRESET key (e.g.
 * `last7`), distinct from the concrete custom bounds `submittedFrom` /
 * `submittedTo`; omitting it would stop a preset choice from sticking. Kept
 * beside the config (not in the route) so this boundary is unit-testable without
 * loading the route's React/router side effects.
 */
export const applicationsSearchSchema = z.object({
  q: z.string().optional(),
  pipelineStage: z.string().optional(),
  coursesPreferred: z.string().optional(),
  hasDocuments: z.string().optional(),
  submitted: z.string().optional(),
  submittedFrom: z.string().optional(),
  submittedTo: z.string().optional(),
  sort: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
});
